<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div class="bg-white dark:bg-secondary-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
        <div>
          <h2 class="text-xl font-bold text-secondary-900 dark:text-white">
            {{ isEditing ? 'Editar firma' : 'Nueva firma' }}
          </h2>
          <p class="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">
            Crea una firma personalizada para tus correos
          </p>
        </div>
        <button @click="emit('close')" class="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors">
          <X class="w-5 h-5 text-secondary-500" />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <!-- Nombre -->
        <div>
          <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
            Nombre de la firma
          </label>
          <input
            v-model="form.name"
            type="text"
            placeholder="Ej: Firma profesional, Firma personal..."
            class="w-full px-3 py-2 border border-secondary-200 dark:border-secondary-700 rounded-lg bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>

        <!-- Firma predeterminada -->
        <div class="flex items-center gap-3">
          <button
            type="button"
            @click="form.isDefault = !form.isDefault"
            class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            :class="form.isDefault ? 'bg-primary-600' : 'bg-secondary-200 dark:bg-secondary-700'"
          >
            <span
              class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              :class="form.isDefault ? 'translate-x-5' : 'translate-x-0'"
            />
          </button>
          <div>
            <p class="text-sm font-medium text-secondary-700 dark:text-secondary-300">Firma predeterminada</p>
            <p class="text-xs text-secondary-400 dark:text-secondary-500">Se insertará automáticamente al redactar un correo</p>
          </div>
        </div>

        <!-- Editor TipTap -->
        <div>
          <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
            Contenido de la firma
          </label>
          <div class="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
            <!-- Toolbar -->
            <RichTextToolbar
              v-if="editor"
              class="px-3 bg-secondary-50 dark:bg-secondary-900"
              :active-formats="activeFormats"
              @toggle="toggleFormat"
              @font-family="editor!.chain().focus().setFontFamily($event).run()"
              @font-size="editor!.chain().focus().setFontSize(`${$event}px`).run()"
              @text-color="editor!.chain().focus().setColor($event).run()"
              @highlight="setHighlight"
              @clear-format="editor!.chain().focus().unsetAllMarks().run()"
              :read-link-context="readLinkContext"
              @link="setLink"
              @unlink="removeLink"
            >
              <template #extra>
                <div class="w-px h-[18px] bg-secondary-200 dark:bg-secondary-700 mx-1"></div>

                <!-- Imagen desde archivo -->
                <button
                  @mousedown.prevent
                  @click="triggerImageUpload"
                  class="p-1.5 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded transition-colors"
                  title="Subir imagen desde dispositivo"
                >
                  <Upload class="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                </button>
                <!-- Imagen desde URL -->
                <button
                  @mousedown.prevent
                  @click="insertImageFromUrl"
                  class="p-1.5 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded transition-colors"
                  title="Insertar imagen desde URL"
                >
                  <ImageIcon class="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                </button>

                <input
                  ref="imageInputRef"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleImageUpload"
                />

                <!-- Separador horizontal -->
                <button
                  @mousedown.prevent
                  @click="editor!.chain().focus().setHorizontalRule().run()"
                  class="p-1.5 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded transition-colors"
                  title="Línea separadora"
                >
                  <Minus class="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                </button>
              </template>
            </RichTextToolbar>

            <!-- TipTap Editor -->
            <EditorContent
              :editor="editor"
              class="signature-editor"
            />
          </div>

          <!-- Image resize controls -->
          <div v-if="selectedImage" class="mt-2 flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <ImageIcon class="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <div class="flex items-center gap-2 flex-1">
              <label class="text-xs font-medium text-blue-700 dark:text-blue-300">Ancho:</label>
              <input
                v-model.number="imageWidth"
                type="number"
                min="20"
                max="600"
                step="10"
                class="w-20 px-2 py-1 text-xs border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white"
              />
              <span class="text-xs text-blue-500">px</span>
              <input
                v-model.number="imageWidth"
                type="range"
                min="20"
                max="600"
                step="10"
                class="flex-1 h-1.5 accent-blue-600"
              />
            </div>
            <button
              @click="applyImageSize"
              class="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Aplicar
            </button>
            <button
              @click="selectedImage = null"
              class="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded transition-colors"
            >
              <X class="w-3.5 h-3.5 text-blue-500" />
            </button>
          </div>
        </div>

        <!-- Preview -->
        <div v-if="form.bodyHtml && form.bodyHtml !== '<p></p>'" class="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 bg-secondary-50 dark:bg-secondary-900/50">
          <p class="text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase mb-2">Vista previa</p>
          <div
            class="signature-preview-content text-sm text-secondary-700 dark:text-secondary-300"
            style="border-top: 1px solid #ccc; padding-top: 12px; color: #666; font-size: 13px;"
          ></div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50">
        <button
          @click="emit('close')"
          class="px-4 py-2 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors text-sm font-medium"
        >
          Cancelar
        </button>
        <button
          @click="handleSave"
          :disabled="!canSave || isSaving"
          class="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
        >
          <Save class="w-4 h-4" />
          {{ isSaving ? 'Guardando...' : (isEditing ? 'Guardar cambios' : 'Crear firma') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import {
  X, Image as ImageIcon, Upload, Minus, Save,
} from 'lucide-vue-next'
import RichTextToolbar from '../editor/RichTextToolbar.vue'
import { buildLinkHtml, normalizeUrl, type ActiveFormats, type ToggleCommand } from '@/composables/useRichTextFormat'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import TiptapImage from '@tiptap/extension-image'
import TiptapLink from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import TiptapUnderline from '@tiptap/extension-underline'
import { TextStyle, FontFamily, FontSize, Color, BackgroundColor } from '@tiptap/extension-text-style'
import Placeholder from '@tiptap/extension-placeholder'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { useEmailSignatureStore } from '@/stores/emailSignature'
import { useAuthStore } from '@/stores/auth'
import type { EmailSignature } from '@/types/emailSignature'

const props = defineProps<{
  signature?: EmailSignature | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved', signature: EmailSignature): void
}>()

const signatureStore = useEmailSignatureStore()
const authStore = useAuthStore()

const isEditing = computed(() => !!props.signature?.id)
const isSaving = ref(false)
const imageInputRef = ref<HTMLInputElement | null>(null)
const selectedImage = ref<HTMLImageElement | null>(null)
const imageWidth = ref(200)

const form = ref({
  name: '',
  bodyHtml: '',
  isDefault: false,
})

const canSave = computed(() => {
  return form.value.name.trim() !== '' && form.value.bodyHtml.trim() !== '' && form.value.bodyHtml !== '<p></p>'
})

const ResizableImage = TiptapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        renderHTML(attributes) {
          if (!attributes.width) return {}
          return { width: attributes.width, style: `width: ${attributes.width}px` }
        },
      },
      height: {
        default: null,
        renderHTML(attributes) {
          if (!attributes.height) return {}
          return { height: attributes.height }
        },
      },
    }
  },
})

// Extend TableCell to preserve inline styles from Gmail signatures
const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('style'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.style) return {}
          return { style: attributes.style }
        },
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('width'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.width) return {}
          return { width: attributes.width }
        },
      },
      colspan: {
        default: 1,
        parseHTML: (element: HTMLElement) => {
          const colspan = element.getAttribute('colspan')
          return colspan ? parseInt(colspan, 10) : 1
        },
      },
      rowspan: {
        default: 1,
        parseHTML: (element: HTMLElement) => {
          const rowspan = element.getAttribute('rowspan')
          return rowspan ? parseInt(rowspan, 10) : 1
        },
      },
      valign: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('valign'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.valign) return {}
          return { valign: attributes.valign }
        },
      },
    }
  },
})

const CustomTable = Table.extend({
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('style'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.style) return {}
          return { style: attributes.style }
        },
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('width'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.width) return {}
          return { width: attributes.width }
        },
      },
      cellpadding: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('cellpadding'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.cellpadding) return {}
          return { cellpadding: attributes.cellpadding }
        },
      },
      cellspacing: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('cellspacing'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.cellspacing) return {}
          return { cellspacing: attributes.cellspacing }
        },
      },
      border: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('border'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.border) return {}
          return { border: attributes.border }
        },
      },
    }
  },
})

const CustomTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('style'),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes.style) return {}
          return { style: attributes.style }
        },
      },
    }
  },
})

const editor = useEditor({
  extensions: [
    StarterKit.configure({
      heading: false,
    }),
    ResizableImage.configure({
      inline: true,
      allowBase64: true,
    }),
    TiptapLink.configure({
      openOnClick: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    TiptapUnderline,
    TextStyle,
    FontFamily,
    FontSize,
    Color,
    BackgroundColor,
    Placeholder.configure({
      placeholder: 'Escribe tu firma aquí... Puedes incluir tu nombre, cargo, teléfono, etc.',
    }),
    CustomTable.configure({
      resizable: false,
    }),
    CustomTableRow,
    CustomTableCell,
    TableHeader,
  ],
  content: props.signature?.bodyHtml || '',
  onUpdate: ({ editor: e }) => {
    form.value.bodyHtml = e.getHTML()
  },
  editorProps: {
    handleClick(view, pos, event) {
      const target = event.target as HTMLElement
      if (target.tagName === 'IMG') {
        selectedImage.value = target as HTMLImageElement
        imageWidth.value = target.width || target.naturalWidth || 200
        return true
      }
      selectedImage.value = null
      return false
    },
  },
})

watch(() => form.value.bodyHtml, (html) => {
  nextTick(() => {
    const previewEl = document.querySelector('.signature-preview-content')
    if (previewEl) {
      previewEl.textContent = ''
      const temp = document.createElement('div')
      temp.innerHTML = html
      while (temp.firstChild) {
        previewEl.appendChild(temp.firstChild)
      }
    }
  })
})

onMounted(() => {
  if (props.signature) {
    form.value.name = props.signature.name
    form.value.bodyHtml = props.signature.bodyHtml
    form.value.isDefault = props.signature.isDefault
  }
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

/** Estado de la barra compartida, leído del editor de Tiptap. */
const activeFormats = computed<ActiveFormats>(() => ({
  bold: editor.value?.isActive('bold') ?? false,
  italic: editor.value?.isActive('italic') ?? false,
  underline: editor.value?.isActive('underline') ?? false,
  strike: editor.value?.isActive('strike') ?? false,
  bulletList: editor.value?.isActive('bulletList') ?? false,
  orderedList: editor.value?.isActive('orderedList') ?? false,
  alignLeft: editor.value?.isActive({ textAlign: 'left' }) ?? false,
  alignCenter: editor.value?.isActive({ textAlign: 'center' }) ?? false,
  alignRight: editor.value?.isActive({ textAlign: 'right' }) ?? false,
}))

/** Traduce los comandos de la barra a los de Tiptap. */
function toggleFormat(command: ToggleCommand) {
  const chain = editor.value?.chain().focus()
  if (!chain) return

  switch (command) {
    case 'bold': chain.toggleBold().run(); break
    case 'italic': chain.toggleItalic().run(); break
    case 'underline': chain.toggleUnderline().run(); break
    case 'strike': chain.toggleStrike().run(); break
    case 'bulletList': chain.toggleBulletList().run(); break
    case 'orderedList': chain.toggleOrderedList().run(); break
    case 'alignLeft': chain.setTextAlign('left').run(); break
    case 'alignCenter': chain.setTextAlign('center').run(); break
    case 'alignRight': chain.setTextAlign('right').run(); break
  }
}

function setHighlight(color: string | null) {
  const chain = editor.value?.chain().focus()
  if (!chain) return
  if (color) chain.setBackgroundColor(color).run()
  else chain.unsetBackgroundColor().run()
}

/** Lo que el diálogo de enlace muestra al abrirse. */
function readLinkContext() {
  if (!editor.value) return { url: '', text: '' }
  const { from, to } = editor.value.state.selection
  return {
    url: editor.value.getAttributes('link').href || '',
    text: editor.value.state.doc.textBetween(from, to, ' '),
  }
}

function setLink({ url, text }: { url: string; text: string }) {
  const href = normalizeUrl(url)
  if (!href || !editor.value) return

  const { from, to } = editor.value.state.selection
  const seleccionado = editor.value.state.doc.textBetween(from, to, ' ')
  const etiqueta = text.trim()

  // Sin selección, o con un texto distinto al que hay, hay que escribir el
  // enlace completo; si el texto es el mismo, basta con marcarlo y así conserva
  // el formato que ya tenía.
  if (from === to || (etiqueta && etiqueta !== seleccionado)) {
    editor.value.chain().focus().insertContent(buildLinkHtml(href, etiqueta)).run()
  } else {
    editor.value.chain().focus().extendMarkRange('link').setLink({ href }).run()
  }
}

function removeLink() {
  editor.value?.chain().focus().extendMarkRange('link').unsetLink().run()
}

function triggerImageUpload() {
  imageInputRef.value?.click()
}

function handleImageUpload(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file || !editor.value) return

  if (file.size > 2 * 1024 * 1024) {
    alert('La imagen no puede superar 2MB. Usa una imagen más pequeña o inserta una URL.')
    input.value = ''
    return
  }

  const reader = new FileReader()
  reader.onload = () => {
    const result = reader.result as string

    const img = new Image()
    img.onload = () => {
      let width = img.naturalWidth
      if (width > 400) width = 400

      editor.value!.chain().focus().setImage({
        src: result,
        alt: file.name,
        // @ts-ignore - custom attribute from ResizableImage
        width,
      }).run()
    }
    img.src = result
  }
  reader.readAsDataURL(file)
  input.value = ''
}

function insertImageFromUrl() {
  if (!editor.value) return

  const url = prompt('Ingrese la URL de la imagen:')
  if (!url) return

  const widthStr = prompt('Ancho de la imagen en pixeles (dejar vacío para auto):', '200')
  const width = widthStr ? parseInt(widthStr, 10) : null

  editor.value.chain().focus().setImage({
    src: url,
    alt: 'Imagen de firma',
    // @ts-ignore - custom attribute from ResizableImage
    ...(width ? { width } : {}),
  }).run()
}

function applyImageSize() {
  if (!selectedImage.value || !editor.value) return

  const { state, dispatch } = editor.value.view
  const { doc, tr } = state

  doc.descendants((node, pos) => {
    if (node.type.name === 'image' && node.attrs.src === selectedImage.value?.getAttribute('src')) {
      dispatch(tr.setNodeMarkup(pos, undefined, {
        ...node.attrs,
        width: imageWidth.value,
      }))
      return false
    }
  })

  selectedImage.value = null
}

async function handleSave() {
  if (!canSave.value) return
  isSaving.value = true

  try {
    const userId = authStore.user?.uid || authStore.user?.id || 'unknown'

    if (isEditing.value && props.signature?.id) {
      const success = await signatureStore.updateSignature(
        props.signature.id,
        {
          name: form.value.name,
          bodyHtml: form.value.bodyHtml,
          isDefault: form.value.isDefault,
        },
        userId
      )

      if (success) {
        emit('saved', {
          ...props.signature,
          ...form.value,
        })
      }
    } else {
      const newId = await signatureStore.createSignature(
        {
          name: form.value.name,
          bodyHtml: form.value.bodyHtml,
          isDefault: form.value.isDefault,
        },
        userId
      )

      if (newId) {
        const created = signatureStore.signatures.find((s) => s.id === newId)
        if (created) emit('saved', created)
      }
    }
  } catch (err) {
    console.error('Error saving signature:', err)
  } finally {
    isSaving.value = false
  }
}
</script>

<style>
.signature-editor .tiptap {
  min-height: 160px;
  max-height: 300px;
  overflow-y: auto;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.6;
  outline: none;
}

.signature-editor .tiptap p {
  margin: 0 0 4px 0;
}

.signature-editor .tiptap img {
  cursor: pointer;
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  transition: outline 0.15s;
}

.signature-editor .tiptap img:hover {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.signature-editor .tiptap img.ProseMirror-selectednode {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.signature-editor .tiptap p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}

.signature-editor .tiptap hr {
  border: none;
  border-top: 1px solid #d1d5db;
  margin: 12px 0;
}

.signature-editor .tiptap a {
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
}

.signature-editor .tiptap ul {
  list-style-type: disc;
  padding-left: 1.5em;
  margin: 4px 0;
}

/* Preserve Gmail/Outlook table-based signature layouts */
.signature-editor .tiptap table {
  border-collapse: collapse;
  width: auto;
}

.signature-editor .tiptap table td,
.signature-editor .tiptap table th {
  border: none;
  padding: 0;
  vertical-align: top;
}

.signature-editor .tiptap table p {
  margin: 0;
}

.signature-preview-content table {
  border-collapse: collapse;
  width: auto;
}

.signature-preview-content table td,
.signature-preview-content table th {
  border: none;
  padding: 0;
  vertical-align: top;
}

.signature-preview-content table p {
  margin: 0;
}
</style>
